import React, { useMemo } from 'react';

/**
 * Proportional floor-plan visual for a listing's Room Sizes.
 *
 * Each `{name, size, note}` entry with a parseable "N.Nm x N.Nm" size
 * becomes a coloured rectangle whose width × height reflects its true
 * dimensions. Rooms are grouped under the previous section-header row
 * (a room entry with a name but no size/note) so multi-storey homes
 * lay out floor-by-floor.
 *
 * Colour palette is a lightweight heuristic on the room name — kitchens
 * warm, bathrooms blue, bedrooms violet, etc. — so the layout is
 * scannable at a glance without needing a legend.
 *
 * Used on:
 *   • PropertyDetailPage — public IE/UK/US detail view
 *   • AdminAddPropertyForm — live preview of scraped/edited rooms
 */

const SIZE_RX = /(\d+(?:\.\d+)?)\s*m\s*(?:x|×)\s*(\d+(?:\.\d+)?)\s*m/i;

const parseDims = (size) => {
  if (!size) return null;
  const m = SIZE_RX.exec(size);
  if (!m) return null;
  const w = parseFloat(m[1]);
  const h = parseFloat(m[2]);
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
  return { w, h };
};

// ── Unit helpers ────────────────────────────────────────────────────────
const M_TO_FT = 3.28084;
const fmt = (v) => (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, '');
export const formatDim = (metres, unit) => {
  if (unit === 'ft') return `${fmt(metres * M_TO_FT)}ft`;
  return `${fmt(metres)}m`;
};

/** Sum floor space (m²) of all rooms with a parseable size. */
export const roomsTotalSqm = (rooms) => {
  let total = 0;
  for (const r of rooms || []) {
    const d = parseDims(r?.size);
    if (d) total += d.w * d.h;
  }
  return total;
};

const colourFor = (name = '') => {
  const n = name.toLowerCase();
  if (/kitchen|breakfast|dining|utility|larder|pantry/.test(n))
    return { bg: 'bg-amber-50',   ring: 'ring-amber-300',   text: 'text-amber-900',   accent: 'text-amber-700' };
  if (/bath|shower|ensuite|w\.?c\.?|cloak/.test(n))
    return { bg: 'bg-sky-50',     ring: 'ring-sky-300',     text: 'text-sky-900',     accent: 'text-sky-700' };
  if (/bed|master/.test(n))
    return { bg: 'bg-violet-50',  ring: 'ring-violet-300',  text: 'text-violet-900',  accent: 'text-violet-700' };
  if (/living|drawing|family|lounge|reception|sitting|snug|den/.test(n))
    return { bg: 'bg-emerald-50', ring: 'ring-emerald-300', text: 'text-emerald-900', accent: 'text-emerald-700' };
  if (/hall|landing|entrance|porch|corridor|stair|foyer|return/.test(n))
    return { bg: 'bg-stone-50',   ring: 'ring-stone-300',   text: 'text-stone-800',   accent: 'text-stone-600' };
  if (/garden|patio|terrace|balcony|garage|shed|store|attic|basement/.test(n))
    return { bg: 'bg-lime-50',    ring: 'ring-lime-300',    text: 'text-lime-900',    accent: 'text-lime-700' };
  if (/office|study|library/.test(n))
    return { bg: 'bg-indigo-50',  ring: 'ring-indigo-300',  text: 'text-indigo-900',  accent: 'text-indigo-700' };
  return { bg: 'bg-white', ring: 'ring-gray-300', text: 'text-gray-800', accent: 'text-gray-500' };
};

/**
 * Group rooms by their preceding section header. Section headers are
 * detected as rows that have a name but no parseable size or note —
 * matches the shape the URL scraper emits (e.g. "GARDEN LEVEL").
 */
const buildGroups = (rooms) => {
  const groups = [];
  let current = { header: '', items: [] };
  for (const r of rooms || []) {
    const dims = parseDims(r.size);
    const isHeader = r.name && !dims && !(r.note || '').trim();
    if (isHeader) {
      if (current.items.length || current.header) groups.push(current);
      current = { header: r.name, items: [] };
      continue;
    }
    if (dims) {
      current.items.push({ ...r, w: dims.w, h: dims.h });
    }
  }
  if (current.items.length) groups.push(current);
  return groups.filter((g) => g.items.length > 0);
};

export const RoomFloorplanVisual = ({
  rooms,
  compact = false,
  defaultUnit = 'm',
  showUnitToggle = false,
  onPrint = null,
}) => {
  const groups = useMemo(() => buildGroups(rooms), [rooms]);
  const [unit, setUnit] = React.useState(defaultUnit);

  // Persist chosen unit per browser so the buyer's preference sticks
  // between listings (only when the toggle is visible).
  React.useEffect(() => {
    if (!showUnitToggle) return;
    try {
      const saved = window.localStorage.getItem('lovina.roomUnit');
      if (saved === 'm' || saved === 'ft') setUnit(saved);
    } catch (_e) { /* ignore */ }
  }, [showUnitToggle]);
  const setPersistUnit = (u) => {
    setUnit(u);
    try { window.localStorage.setItem('lovina.roomUnit', u); } catch (_e) { /* ignore */ }
  };

  // Scale metres → pixels. Compact mode (admin preview) uses a smaller
  // px-per-metre so the whole plan fits in the sidebar.
  const pxPerMetre = compact ? 16 : 22;

  if (!groups.length) return null;

  return (
    <div className="space-y-5" data-testid="room-floorplan-visual">
      {(showUnitToggle || onPrint) && (
        <div className="flex items-center justify-end gap-2" data-testid="floorplan-toolbar">
          {showUnitToggle && (
            <div className="inline-flex rounded-full ring-1 ring-gray-200 bg-white overflow-hidden text-[11px] font-semibold"
                 role="group" aria-label="Room dimension units">
              {['m', 'ft'].map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setPersistUnit(u)}
                  className={`px-3 py-1 transition-colors ${unit === u ? 'bg-[#7042cc] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  data-testid={`floorplan-unit-${u}`}
                  aria-pressed={unit === u}
                >
                  {u === 'm' ? 'Metres' : 'Feet'}
                </button>
              ))}
            </div>
          )}
          {onPrint && (
            <button
              type="button"
              onClick={onPrint}
              className="text-[11px] font-semibold text-[#5b35a8] hover:underline px-2 py-1"
              data-testid="floorplan-print-btn"
            >
              Print floor plan
            </button>
          )}
        </div>
      )}
      {groups.map((g, gi) => (
        <div key={gi} data-testid={`floorplan-group-${gi}`}>
          {g.header && (
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-2">
              {g.header}
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-end">
            {g.items.map((r, i) => {
              const c = colourFor(r.name);
              const width  = Math.max(compact ? 56 : 74, r.w * pxPerMetre);
              const height = Math.max(compact ? 44 : 58, r.h * pxPerMetre);
              return (
                <div
                  key={i}
                  className={`${c.bg} ring-1 ${c.ring} rounded-md ${compact ? 'p-1.5' : 'p-2'} flex flex-col justify-between shrink-0 transition-shadow hover:shadow-md`}
                  style={{ width: `${width}px`, height: `${height}px` }}
                  title={r.note || `${r.name} — ${formatDim(r.w, unit)} × ${formatDim(r.h, unit)}`}
                  data-testid={`floorplan-room-${gi}-${i}`}
                >
                  <div className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-bold leading-tight ${c.text}`}>
                    {r.name}
                  </div>
                  <div className={`${compact ? 'text-[9px]' : 'text-[10px]'} ${c.accent} font-mono`}>
                    {formatDim(r.w, unit)} × {formatDim(r.h, unit)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-gray-400 italic">
        Blocks are drawn to scale — approximate; refer to floor plan for exact dimensions.
      </p>
    </div>
  );
};

export default RoomFloorplanVisual;

// ── Print helper ────────────────────────────────────────────────────────
// Opens a self-contained new-window document with the address + agent
// header and a print-ready floor-plan. Inline styles only so it works
// without the app's CSS.
const HEX = {
  amber:   { bg: '#fef3c7', ring: '#fcd34d', ink: '#78350f' },
  sky:     { bg: '#e0f2fe', ring: '#7dd3fc', ink: '#0c4a6e' },
  violet:  { bg: '#f3e8ff', ring: '#c4b5fd', ink: '#4c1d95' },
  emerald: { bg: '#d1fae5', ring: '#6ee7b7', ink: '#064e3b' },
  stone:   { bg: '#f5f5f4', ring: '#d6d3d1', ink: '#292524' },
  lime:    { bg: '#ecfccb', ring: '#bef264', ink: '#365314' },
  indigo:  { bg: '#e0e7ff', ring: '#a5b4fc', ink: '#312e81' },
  neutral: { bg: '#ffffff', ring: '#d1d5db', ink: '#374151' },
};
const hexFor = (name = '') => {
  const n = name.toLowerCase();
  if (/kitchen|breakfast|dining|utility|larder|pantry/.test(n)) return HEX.amber;
  if (/bath|shower|ensuite|w\.?c\.?|cloak/.test(n)) return HEX.sky;
  if (/bed|master/.test(n)) return HEX.violet;
  if (/living|drawing|family|lounge|reception|sitting|snug|den/.test(n)) return HEX.emerald;
  if (/hall|landing|entrance|porch|corridor|stair|foyer|return/.test(n)) return HEX.stone;
  if (/garden|patio|terrace|balcony|garage|shed|store|attic|basement/.test(n)) return HEX.lime;
  if (/office|study|library/.test(n)) return HEX.indigo;
  return HEX.neutral;
};

export const openFloorplanPrint = ({ property, unit = 'm' }) => {
  const rooms = property?.rooms || [];
  const groups = buildGroups(rooms);
  if (!groups.length) return;

  const pxPerMetre = 20;
  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const groupHtml = groups.map((g) => {
    const items = g.items.map((r) => {
      const c = hexFor(r.name);
      const w = Math.max(80, r.w * pxPerMetre);
      const h = Math.max(56, r.h * pxPerMetre);
      return `<div style="background:${c.bg};border:1px solid ${c.ring};color:${c.ink};border-radius:6px;padding:6px;width:${w}px;height:${h}px;display:flex;flex-direction:column;justify-content:space-between;font-family:system-ui,sans-serif;box-sizing:border-box">
        <div style="font-size:10px;font-weight:700;line-height:1.15">${esc(r.name)}</div>
        <div style="font-size:9px;font-family:ui-monospace,Menlo,monospace;opacity:.8">${esc(formatDim(r.w, unit))} × ${esc(formatDim(r.h, unit))}</div>
      </div>`;
    }).join('');
    const header = g.header
      ? `<div style="font-size:10px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:#6b7280;margin:14px 0 6px">${esc(g.header)}</div>`
      : '';
    return `${header}<div style="display:flex;flex-wrap:wrap;gap:6px;align-items:flex-end">${items}</div>`;
  }).join('');

  const agency = property?.external_agent?.agency
                 || property?.agent?.agency || '';
  const agentName = property?.external_agent?.name
                    || property?.agent?.name || '';
  const doc = `<!doctype html><html><head><meta charset="utf-8"><title>Floor plan — ${esc(property?.title || '')}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { margin:0; padding:14px; font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color:#111827; }
  header { border-bottom:1px solid #e5e7eb; padding-bottom:8px; margin-bottom:12px; }
  h1 { font-size:16px; margin:0 0 4px; }
  .sub { font-size:11px; color:#6b7280; }
  footer { margin-top:18px; padding-top:8px; border-top:1px solid #e5e7eb; font-size:10px; color:#6b7280; display:flex; justify-content:space-between; }
  .brand { color:#5b35a8; font-weight:700; }
</style></head>
<body>
  <header>
    <h1>${esc(property?.title || 'Floor Plan')}</h1>
    <div class="sub">${esc(property?.location?.fullAddress || '')}</div>
    ${agentName ? `<div class="sub">Listed by ${esc(agentName)}${agency ? ' — ' + esc(agency) : ''}</div>` : ''}
  </header>
  ${groupHtml}
  <footer>
    <span>Room blocks drawn to scale (${unit === 'ft' ? 'feet' : 'metres'}) — approximate.</span>
    <span class="brand">lovinahome</span>
  </footer>
  <script>window.addEventListener('load', () => setTimeout(() => window.print(), 200));</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.open();
  w.document.write(doc);
  w.document.close();
};
